import { emptyEventFormValues, parseDaysOrNull, ProceduralEventFormValues } from './procedural-event-form';
import {
  effectiveAnchor,
  effectiveView,
  isEffectivelyUnconfirmed,
  mapCountingToEvent,
  mapDeltaTypeToCounting,
} from './procedural-helpers';
import type { EditSuggestionProposalBody } from '../../../../../../../../backend/src/matter/procedural-timeline/dto/edit-suggestion-proposal.request.dto';
import { DeltaIntervalType } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-enums';
import type { ProceduralTimelineEventSuggestion } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event-suggestion.entity';
import { combineDateAndTime, splitDateAndTime } from '@/new/components/timeline-calculator/civil-date';

/** Seed the shared event form from a suggestion's effective (post-accept) view. */
export function suggestionToFormValues(suggestion: ProceduralTimelineEventSuggestion): ProceduralEventFormValues {
  const view = effectiveView(suggestion);
  const anchor = effectiveAnchor(suggestion);
  const jurisdiction =
    suggestion.kind === 'add' ? suggestion.proposedJurisdiction : (suggestion.existingEvent?.jurisdiction ?? null);
  const start = splitDateAndTime(view.startDateTime);
  const end = splitDateAndTime(view.endDateTime);

  return {
    ...emptyEventFormValues(),
    name: view.name === '(unnamed)' || view.name === '(unknown event)' ? '' : view.name,
    description: view.description ?? '',
    timeZone: view.timeZone ?? '',
    startDate: start.date,
    startTime: start.time,
    endDate: end.date,
    endTime: end.time,
    anchorEventId: anchor.anchorEventId ?? '',
    deltaDays: anchor.interval?.days !== undefined ? String(anchor.interval.days) : '',
    countingKey: mapDeltaTypeToCounting(anchor.intervalType ?? DeltaIntervalType.Calendar, jurisdiction),
    isConfirmed: !isEffectivelyUnconfirmed(suggestion),
  };
}

/** Full mapping of form state to a proposal body (before diffing). */
function formValuesToProposal(v: ProceduralEventFormValues): EditSuggestionProposalBody {
  const anchored = v.anchorEventId !== '';
  const parsedDays = parseDaysOrNull(v.deltaDays);
  const counting = mapCountingToEvent(v.countingKey);
  return {
    proposedName: v.name.trim(),
    proposedDescription: v.description,
    proposedTimeZone: v.timeZone,
    // Cleared values are sent as null (not undefined) so the diff carries them
    // through JSON and the backend records the clear instead of keeping the old value.
    proposedStartDateTime: v.startDate !== null ? combineDateAndTime(v.startDate, v.startTime) : null,
    proposedEndDateTime: v.endDate !== null ? combineDateAndTime(v.endDate, v.endTime) : null,
    proposedJurisdiction: anchored ? counting.jurisdiction : undefined,
    proposedAnchorExistingEventId: anchored ? v.anchorEventId : null,
    proposedDeltaInterval: anchored && parsedDays !== null ? { days: parsedDays } : null,
    proposedDeltaIntervalType: anchored ? counting.deltaIntervalType : undefined,
    proposedIsConfirmed: v.isConfirmed,
  };
}

/** The proposal fields the reviewer actually changed (leaves the rest untouched on accept). */
export function changedProposal(
  initial: ProceduralEventFormValues,
  currentValues: ProceduralEventFormValues,
): EditSuggestionProposalBody {
  const before = formValuesToProposal(initial) as Record<string, unknown>;
  const after = formValuesToProposal(currentValues) as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(after)) {
    if (JSON.stringify(after[key]) !== JSON.stringify(before[key])) out[key] = after[key];
  }
  return out as EditSuggestionProposalBody;
}
